// src/lib/supabase.js
// Supabase client configuration

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Phase R.2.4 — Sanitize a deal name for SharePoint compatibility.
// `/` and `\` create unintended folder hierarchy in SP (paths are
// constructed as `Acquisitions/<deal.name>/...`), so we strip them at
// every save point. Returns { name, changed } so the caller can decide
// whether to toast a heads-up to the user.
//
// Replacement chosen: ` - ` reads naturally for two-name combinations
// ("Magnolia / Scott" → "Magnolia - Scott"). Multiple consecutive
// spaces collapse so we don't end up with awkward "  -  " runs.
export function sanitizeDealName(raw) {
  const original = String(raw || '').trim();
  const cleaned = original
    .replace(/\s*[\\/]+\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim();
  return { name: cleaned, changed: cleaned !== original };
}

// Phase R.2.5 — Mirror the edge function's safeSegment() so client-side
// "Open SharePoint" URLs point at the folder Graph actually created.
// Trailing dots / spaces and forbidden chars are stripped from each
// path segment. Use when constructing SP folder URLs for the browser.
export function sharePointSafeSegment(name) {
  return String(name || '')
    .replace(/[*:<>?\\|"]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[.\s]+$/g, '')
    .replace(/^[.\s]+/g, '')
    .trim();
}

// Helper to get current user's organization
export async function getCurrentUserOrg() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  
  const { data } = await supabase
    .from('users')
    .select('organization_id, role, organizations(*)')
    .eq('id', user.id)
    .single();
    
  return data;
}

// Helper to upload file to storage (authenticated users)
export async function uploadFile(file, dealId, taskId, organizationId) {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  const filePath = `${organizationId}/${dealId}/${taskId}/${fileName}`;
  
  const { data, error } = await supabase.storage
    .from('deal-documents')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    });
    
  if (error) throw error;
  
  return {
    path: data.path,
    name: fileName,
    originalName: file.name,
    size: file.size,
    mimeType: file.type
  };
}

// Helper to upload file from seller portal (no auth required)
export async function uploadSellerFile(file, dealId, taskId) {
  const fileExt = file.name.split('.').pop();
  const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
  // Seller uploads go to a special folder
  const filePath = `seller-uploads/${dealId}/${taskId}/${fileName}`;
  
  const { data, error } = await supabase.storage
    .from('deal-documents')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false
    });
    
  if (error) throw error;
  
  return {
    path: data.path,
    name: fileName,
    originalName: file.name,
    size: file.size,
    mimeType: file.type
  };
}

// Helper to get signed URL for file download
export async function getFileUrl(storagePath) {
  const { data, error } = await supabase.storage
    .from('deal-documents')
    .createSignedUrl(storagePath, 3600); // 1 hour expiry
    
  if (error) throw error;
  return data.signedUrl;
}

// Helper to delete file from storage
export async function deleteFileFromStorage(storagePath) {
  const { error } = await supabase.storage
    .from('deal-documents')
    .remove([storagePath]);
    
  if (error) throw error;
}

// ============================================================================
// EMAIL FUNCTIONS
// ============================================================================

/**
 * Send an email via Supabase Edge Function (Resend)
 * @param {Object} options
 * @param {string} options.to - Recipient email address
 * @param {string} options.subject - Email subject
 * @param {string} options.text - Plain text body
 * @param {string} [options.html] - HTML body (optional, will convert text to HTML if not provided)
 * @param {string} [options.replyTo] - Reply-to address (optional)
 * @returns {Promise<{success: boolean, id?: string, error?: string}>}
 */
export async function sendEmail({ to, cc, bcc, subject, text, html, replyTo }) {
  try {
    // Get the current session for the auth token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Not authenticated');
    }

    // Call the Edge Function with explicit auth header
    const response = await fetch(
      `${supabaseUrl}/functions/v1/send-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          to,
          cc,
          bcc,
          subject,
          text,
          html: html || formatTextToHtml(text),
          replyTo,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send email');
    }

    return { success: true, id: data?.id };
  } catch (error) {
    console.error('Error sending email:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Convert plain text to simple HTML (preserves line breaks and basic formatting)
 */
function formatTextToHtml(text) {
  if (!text) return '';
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
    .replace(/  /g, '&nbsp;&nbsp;')
    // Make URLs clickable
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" style="color: #1A3665;">$1</a>');
}

// ============================================================================
// SELLER PORTAL FUNCTIONS (No authentication required)
// ============================================================================

// Generate a new seller access token for a deal
export async function generateSellerToken(dealId) {
  const { data, error } = await supabase.rpc('generate_seller_token', { 
    p_deal_id: dealId 
  });
  
  if (error) throw error;
  return data;
}

// Generate a scoped seller token for a third-party contact (accountant or broker).
// The returned token, when used at /seller/:token, only exposes doc-request tasks
// whose assigned_to_contact matches the scope, and does not expose business info.
// `scope` must be one of 'seller' | 'accountant' | 'broker'. `email` and `name`
// are optional; `name` is stored and used for uploaded_by_name on files.
export async function generateScopedSellerToken(dealId, scope, email, name) {
  const { data, error } = await supabase.rpc('generate_scoped_seller_token', {
    p_deal_id: dealId,
    p_scope: scope,
    p_scope_email: email || null,
    p_scope_name: name || null
  });

  if (error) throw error;
  return data;
}

// Phase Q.4 — seller-initiated invite for counsel/accountant/broker.
// Holder of a seller-scope token can mint a fresh scoped token for a
// third party on the same deal. Returns { token, expires_at, portal_url }.
export async function sellerInviteThirdParty(sellerToken, scope, email, name) {
  const { data, error } = await supabase.rpc('seller_invite_third_party', {
    p_seller_token: sellerToken,
    p_scope: scope,
    p_email: email,
    p_name: name || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function sellerListThirdPartyInvites(sellerToken) {
  const { data, error } = await supabase.rpc('seller_list_third_party_invites', {
    p_seller_token: sellerToken,
  });
  if (error) throw error;
  return data || [];
}

// Validate a seller token and get deal info
export async function validateSellerToken(token) {
  const { data, error } = await supabase.rpc('validate_seller_token', { 
    p_token: token 
  });
  
  if (error) throw error;
  return data;
}

// Get tasks for seller portal
export async function getSellerTasks(token) {
  const { data, error } = await supabase.rpc('get_seller_tasks', { 
    p_token: token 
  });
  
  if (error) throw error;
  return data;
}

// Record a seller file upload in the database
export async function recordSellerUpload(token, taskId, fileData) {
  // Use database function to bypass RLS
  const { data, error } = await supabase.rpc('record_seller_file_upload', {
    p_p_token: token,
    p_task_id: taskId,
    p_file_name: fileData.name,
    p_original_name: fileData.originalName,
    p_storage_path: fileData.path,
    p_size_bytes: fileData.size,
    p_mime_type: fileData.mimeType
  });
  
  if (error) throw error;
  
  if (!data.success) {
    throw new Error(data.error || 'Upload failed');
  }
  
  return data;
}

// Build seller portal URL
export function buildSellerPortalUrl(token) {
  const baseUrl = window.location.origin;
  return baseUrl + '/seller/' + token;
}

// ============================================================================
// DUE DILIGENCE CHECKLIST — PORTAL + ADMIN (Phase C)
// ============================================================================

// Get DD items visible to the token holder. For seller-scope tokens this
// returns items with assigned_to='seller'; for counsel/accountant/broker
// tokens it returns items assigned to that scope. Third-party scopes use
// this instead of getSellerTasks to drive the portal.
export async function getSellerDdItems(token) {
  const { data, error } = await supabase.rpc('get_seller_dd_items', {
    p_token: token,
  });
  if (error) throw error;
  return data;
}

// Record a portal upload against a specific DD item. Flips the item to
// 'received' server-side. Only called when the user clicked the upload
// button on a specific item (for generic drag-drop we use the existing
// record_seller_general_upload path and let AI auto-match).
export async function recordSellerDdUpload(token, ddItemId, fileData) {
  const { data, error } = await supabase.rpc('record_seller_dd_upload', {
    p_token: token,
    p_dd_item_id: ddItemId,
    p_file_name: fileData.name,
    p_original_name: fileData.originalName,
    p_storage_path: fileData.path,
    p_size_bytes: fileData.size,
    p_mime_type: fileData.mimeType,
  });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Upload failed');
  return data;
}

// Admin-only: move a file between DD items (or unlink by passing null).
// Promotes the target item's status to 'received' if linking.
export async function relinkDdFile(fileId, ddItemId) {
  const { data, error } = await supabase.rpc('relink_dd_file', {
    p_file_id: fileId,
    p_dd_item_id: ddItemId,
  });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Relink failed');
  return data;
}

// Portal: mark a DD item handled without an upload, or mark it N/A.
// Handled requires a non-empty note explaining how it was handled (e.g.
// "Granted QBO access to ..."); N/A's note is optional color on why it
// doesn't apply. Flips the item to 'received' or 'n_a' respectively.
export async function recordSellerDdResolution(token, ddItemId, note, markNa = false) {
  const { data, error } = await supabase.rpc('record_seller_dd_resolution', {
    p_token: token,
    p_dd_item_id: ddItemId,
    p_resolution_note: note,
    p_mark_na: markNa,
  });
  if (error) throw error;
  if (!data?.success) throw new Error(data?.error || 'Could not save');
  return data;
}

// ============================================================================
// SHAREPOINT INTEGRATION HELPERS
// ============================================================================

// Base helper to call the sharepoint-sync edge function
async function callSharePointSync(action, params = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  
  const response = await fetch(
    supabaseUrl + '/functions/v1/sharepoint-sync',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + (session?.access_token || ''),
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ action, ...params }),
    }
  );

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'SharePoint operation failed');
  return data;
}

// Create the full folder structure for a deal
export async function createSharePointDealStructure(dealName, organizationId) {
  return callSharePointSync('create-deal-structure', { dealName, organizationId });
}

// Check if a folder exists and get its URL
export async function getSharePointFolderUrl(folderPath) {
  return callSharePointSync('get-folder-url', { folderPath });
}

// Ensure a folder exists (creates if needed)
export async function ensureSharePointFolder(folderPath) {
  return callSharePointSync('ensure-folder', { folderPath });
}

// Upload a file to SharePoint (with Acquisitions prefix)
export async function uploadToSharePoint(file, folderPath, fileName, categoryId) {
  const arrayBuffer = await file.arrayBuffer();
  const base64 = btoa(
    new Uint8Array(arrayBuffer).reduce(function(data, byte) { return data + String.fromCharCode(byte); }, '')
  );

  return callSharePointSync('upload-file-with-category', {
    dealName: folderPath,
    fileName: fileName || file.name,
    fileContentBase64: base64,
    mimeType: file.type,
    categoryId: categoryId || null,
  });
}

// List contents of a SharePoint folder
export async function listSharePointFolder(folderPath) {
  return callSharePointSync('list-folder', { folderPath: folderPath || '' });
}

// Upload file from seller portal (no user auth, uses seller token)
export async function uploadToSharePointAsSeller(file, sellerToken, dealName, fileName, categoryId) {
  const arrayBuffer = await file.arrayBuffer();
  const base64 = btoa(
    new Uint8Array(arrayBuffer).reduce(function(data, byte) { return data + String.fromCharCode(byte); }, '')
  );

  const response = await fetch(
    supabaseUrl + '/functions/v1/sharepoint-sync',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'upload-file-with-category',
        sellerToken: sellerToken,
        dealName: dealName,
        fileName: fileName || file.name,
        fileContentBase64: base64,
        mimeType: file.type,
        categoryId: categoryId || null,
      }),
    }
  );

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'SharePoint upload failed');
  return data;
}

// ============================================================================
// CALENDAR / VISITS HELPERS
// ============================================================================

// Get the Microsoft token from the current Supabase session
async function getMicrosoftToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.provider_token || null;
}

// Create a calendar event in M365 Outlook
export async function createCalendarEvent({ subject, start, end, location, body }) {
  try {
    const token = await getMicrosoftToken();
    if (!token) throw new Error('No Microsoft token available. Please sign in with Microsoft.');

    const response = await fetch('https://graph.microsoft.com/v1.0/me/events', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subject: subject,
        start: { dateTime: start, timeZone: 'Eastern Standard Time' },
        end: { dateTime: end, timeZone: 'Eastern Standard Time' },
        location: { displayName: location || '' },
        body: { contentType: 'text', content: body || '' },
      }),
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error?.message || 'Failed to create event');
    }

    const event = await response.json();
    return { success: true, eventId: event.id, webLink: event.webLink };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

// Cancel a calendar event
export async function cancelCalendarEvent(visitId, m365EventId, userId) {
  try {
    const token = await getMicrosoftToken();
    if (token && m365EventId) {
      const response = await fetch('https://graph.microsoft.com/v1.0/me/events/' + m365EventId, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token },
      });
      if (!response.ok && response.status !== 404) {
        console.error('Failed to delete M365 event:', response.status);
      }
    }

    const { error } = await supabase
      .from('deal_visits')
      .update({ status: 'canceled', canceled_at: new Date().toISOString(), canceled_by: userId })
      .eq('id', visitId);
    if (error) throw error;

    return { success: true };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

// Get visits for a specific deal
export async function getDealVisits(dealId) {
  try {
    const { data, error } = await supabase
      .from('deal_visits')
      .select('*')
      .eq('deal_id', dealId)
      .order('visit_date', { ascending: true })
      .order('start_time', { ascending: true });
    if (error) throw error;
    return { success: true, visits: data };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

// Get upcoming visits for an organization (for activity feed)
export async function getUpcomingVisits(organizationId, days) {
  try {
    var lookAhead = days || 30;
    var today = new Date().toISOString().split('T')[0];
    var futureDate = new Date(Date.now() + lookAhead * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('deal_visits')
      .select('*, deals(name, stage)')
      .eq('organization_id', organizationId)
      .eq('status', 'scheduled')
      .gte('visit_date', today)
      .lte('visit_date', futureDate)
      .order('visit_date', { ascending: true })
      .order('start_time', { ascending: true });
    if (error) throw error;
    return { success: true, visits: data };
  } catch (err) {
    return { success: false, error: err.message };
  }
}
// ============================================================================
// BUSINESS INFORMATION REQUEST FUNCTIONS
// Append these to the bottom of src/lib/supabase.js
// ============================================================================

// Get seller info request status + all submitted data (for portal pre-population)
export async function getSellerInfoRequest(token) {
  const { data, error } = await supabase.rpc('get_seller_info_request', {
    p_token: token
  });
  if (error) throw error;
  return data;
}

// Submit a category of business info from the seller portal.
// `removed` identifies rows the caller explicitly deleted from what they
// loaded (uuid[] for id-keyed categories, string[] for natural-key
// categories like utilities/facilities, or {computers,software,other} for
// technology) — anything NOT named here is left untouched server-side,
// even if it's missing from `categoryData`, so a stale-snapshot save can
// never delete a row (e.g. one AI just inserted) the caller never saw.
export async function submitSellerInfo(token, category, categoryData, removed = []) {
  const { data: result, error } = await supabase.rpc('submit_seller_info', {
    p_token: token,
    p_category: category,
    p_data: categoryData,
    p_removed: removed,
  });
  if (error) throw error;
  if (!result.success) throw new Error(result.error || 'Submission failed');
  return result;
}

// Send an info request to the seller (upserts deal_info_requests + emails seller)
export async function sendInfoRequest({ dealId, organizationId, userId, deal, sellerEmail, sellerContact, primaryColor }) {
  // 1. Upsert the info request record
  const { error: upsertError } = await supabase
    .from('deal_info_requests')
    .upsert({
      deal_id: dealId,
      organization_id: organizationId,
      sent_by: userId,
      status: 'pending',
      sent_at: new Date().toISOString(),
    }, { onConflict: 'deal_id' });

  if (upsertError) throw upsertError;

  // 2. Generate a seller token for the portal link
  const tokenResult = await generateSellerToken(dealId);
  const token = Array.isArray(tokenResult) ? tokenResult[0].token : tokenResult.token;
  if (!token) throw new Error('Failed to generate seller token');
  const portalUrl = buildSellerPortalUrl(token);
  const color = primaryColor || '#1A3665';
  const dealName = deal?.name || 'the transaction';
  const contactName = sellerContact || 'there';

  // 3. Send the email
  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <div style="background: ${color}; padding: 24px 32px;">
        <h1 style="color: white; margin: 0; font-size: 22px;">Business Information Request</h1>
        <p style="color: rgba(255,255,255,0.85); margin: 6px 0 0;">Landmark Funeral Group</p>
      </div>
      <div style="padding: 32px; background: #ffffff; border: 1px solid #e5e7eb; border-top: none;">
        <p style="font-size: 16px; color: #374151;">Hi ${contactName},</p>
        <p style="color: #6b7280; line-height: 1.6;">
          As part of our due diligence process for <strong>${dealName}</strong>, we'd like to collect
          some structured information about the business. This covers staffing, vehicles, utilities,
          preneed contracts, facilities, crematory equipment, and technology.
        </p>
        <p style="color: #6b7280; line-height: 1.6;">
          You can fill this out at your own pace — each section saves independently, and you can
          return to the portal anytime using the same link.
        </p>
        <div style="text-align: center; margin: 32px 0;">
          <a href="${portalUrl}" style="background: ${color}; color: white; padding: 14px 32px;
            border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 16px;
            display: inline-block;">
            Open Information Portal &#8594;
          </a>
        </div>
        <p style="color: #9ca3af; font-size: 13px; text-align: center;">
          This link is unique to you and will remain active during the diligence process.
        </p>
        <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 24px 0;" />
        <p style="color: #9ca3af; font-size: 12px;">
          Questions? Reply to this email or contact your deal representative directly.
        </p>
      </div>
    </div>
  `;

  const emailResult = await sendEmail({
    to: sellerEmail,
    subject: `Business Information Request \u2014 ${dealName}`,
    text: `Hi ${contactName},\n\nWe'd like to collect some business information for ${dealName}. Please use this link to complete the form:\n\n${portalUrl}\n\nYou can fill it out at your own pace and return anytime with the same link.\n\nQuestions? Reply to this email.`,
    html,
  });

  return { success: true, token, portalUrl, emailResult };
}

// Business Info tables use column names that don't always match the form
// field names the UI has always used (e.g. deal_vehicles.balance_owed vs
// the form's loan_balance, deal_preneed.contact vs contact_name/
// contact_phone). Both DealView's edit modal AND the seller portal's own
// form fields expect the "rich" names below — normalize every read here
// so both read paths (getInfoRequestByDealId + DealView's own fallback
// fetch) produce identical, form-ready shapes without duplicating this
// mapping in two places. Adds aliases alongside the raw columns rather
// than replacing them, so any code still reading the raw name keeps working.
export function normalizeBusinessInfoRows({ personnel, vehicles, utilities, preneed, facilities, crematory, computers, software, techOther }) {
  return {
    // Rows AI extraction (or flag_deal_personnel_duplicates/
    // flag_deal_vehicle_duplicates) has flagged as a likely duplicate of
    // another row are excluded from the editable form entirely — the same
    // roster re-appearing in every payroll file, month after month, with
    // nothing deduping the inserts, is exactly what produced 1000+
    // personnel rows for ~90 real employees on one deal and crashed a
    // seller's browser trying to render them all. The admin-side
    // "Possible Duplicates" review panel queries these rows directly, so
    // it's unaffected by this filter.
    personnel: (personnel || []).filter(r => !r.possible_duplicate_of),
    vehicles: (vehicles || []).filter(r => !r.possible_duplicate_of).map(r => ({ ...r, loan_balance: r.balance_owed, kbb_value: r.kelly_blue_book })),
    utilities: (utilities || []).map(r => ({ ...r, vendor_name: r.vendor, contract_end: r.contract_details })),
    preneed: (preneed || []).map(r => ({ ...r, contact_name: r.contact, contact_phone: '' })),
    facilities: (facilities || []).map(r => ({ ...r, vendor_phone: r.phone, contract_end: r.contract_details })),
    crematory: crematory || [],
    // Flat shape (not nested under `technology`) to match what the
    // read-only summary and the edit modal have always expected —
    // computers/peripherals split by device_class, older NULL-device_class
    // rows treated as computers for back-compat.
    tech_computers: (computers || []).filter(c => (c.device_class || 'computer') === 'computer'),
    tech_peripherals: (computers || []).filter(c => c.device_class === 'peripheral'),
    tech_software: software || [],
    tech_other: (techOther || []).map(r => ({ ...r, url: '' })),
  };
}

// Get info request status + submitted data for internal DealView (authenticated, no token needed)
export async function getInfoRequestByDealId(dealId) {
  const { data: infoRequest, error } = await supabase
    .from('deal_info_requests')
    .select('*')
    .eq('deal_id', dealId)
    .maybeSingle();

  if (error) throw error;
  if (!infoRequest) return null;

  // Fetch all submitted category data in parallel
  const [personnel, personnelSurvey, vehicles, utilities, preneed, facilities, crematory, computers, software, techOther] = await Promise.all([
    supabase.from('deal_personnel').select('*').eq('deal_id', dealId),
    supabase.from('deal_personnel_survey').select('*').eq('deal_id', dealId).maybeSingle(),
    supabase.from('deal_vehicles').select('*').eq('deal_id', dealId),
    supabase.from('deal_utilities').select('*').eq('deal_id', dealId),
    supabase.from('deal_preneed').select('*').eq('deal_id', dealId),
    supabase.from('deal_facilities').select('*').eq('deal_id', dealId),
    supabase.from('deal_crematory').select('*').eq('deal_id', dealId),
    supabase.from('deal_tech_computers').select('*').eq('deal_id', dealId),
    supabase.from('deal_tech_software').select('*').eq('deal_id', dealId),
    supabase.from('deal_tech_other').select('*').eq('deal_id', dealId),
  ]);

  return {
    info_request: infoRequest,
    submitted_data: {
      ...normalizeBusinessInfoRows({
        personnel: personnel.data, vehicles: vehicles.data, utilities: utilities.data,
        preneed: preneed.data, facilities: facilities.data, crematory: crematory.data,
        computers: computers.data, software: software.data, techOther: techOther.data,
      }),
      personnel_survey: personnelSurvey.data || null,
    },
  };
}

// Save a business info section internally (authenticated, no seller token required)
// Used by LFG team to enter data on behalf of the seller from DealView.
// `removed` mirrors submitSellerInfo's param — rows the caller explicitly
// deleted from what they loaded; anything else absent from `data` (e.g. a
// row auto-categorize-document inserted after the form loaded) is left
// alone rather than deleted.
export async function saveInfoSectionInternal(dealId, organizationId, category, data, removed = []) {
  // Ensure a deal_info_requests record exists
  const { error: upsertErr } = await supabase
    .from('deal_info_requests')
    .upsert(
      { deal_id: dealId, organization_id: organizationId, status: 'pending', sent_at: new Date().toISOString() },
      { onConflict: 'deal_id', ignoreDuplicates: true }
    );
  if (upsertErr) throw upsertErr;

  // Delegate the actual write to a single Postgres function call so it
  // runs as one transaction — a failed insert (bad data, bad column) rolls
  // back its own delete instead of leaving the category wiped with
  // nothing written in its place. This is the same function
  // submit_seller_info uses for the seller-portal path, so the form-field
  // -> real-column mapping only needs to be maintained in one place.
  const { error } = await supabase.rpc('save_business_info_category', {
    p_deal_id: dealId,
    p_organization_id: organizationId,
    p_category: category,
    p_data: category === 'utilities'
      ? data.filter(r => r.vendor_name || r.account_number)
      : category === 'facilities'
        ? data.filter(r => r.vendor_name || r.vendor_phone)
        : data,
    p_removed: removed,
  });
  if (error) throw error;

  // Update categories_submitted + status on deal_info_requests
  const { data: current } = await supabase
    .from('deal_info_requests')
    .select('categories_submitted')
    .eq('deal_id', dealId)
    .single();

  const existing = current?.categories_submitted || [];
  const updated = [...new Set([...existing, category])];
  const { error: updateErr } = await supabase
    .from('deal_info_requests')
    .update({
      categories_submitted: updated,
      status: updated.length >= 7 ? 'complete' : 'partial',
      updated_at: new Date().toISOString(),
    })
    .eq('deal_id', dealId);
  if (updateErr) throw updateErr;

  return { success: true };
}

// ===========================================================================
// GEOCODING
// ===========================================================================
//
// Browser-side geocoding helper. Tries the Census Bureau's free geocoder
// first (no key, no rate limit, CORS-friendly, US-only), then falls back to
// Nominatim (OpenStreetMap) for anything Census can't resolve.
//
// We were on Nominatim-only for a while and it kept failing silently —
// browser fetches don't preserve the User-Agent header Nominatim wants for
// rate-limit accounting, so it 429s/quietly returns empty. Census doesn't
// have that problem.
//
// Returns { latitude, longitude, source } or null.
export async function geocodeAddress(address, city, state, zip) {
  const oneline = [address, city, state && zip ? `${state} ${zip}` : (state || zip || '')]
    .filter(Boolean).join(', ').trim();
  if (!oneline) return null;

  // 1) Census — US addresses only, but that's our entire dataset
  try {
    const url = 'https://geocoding.geo.census.gov/geocoder/locations/onelineaddress?'
      + new URLSearchParams({
          address: oneline,
          benchmark: 'Public_AR_Current',
          format: 'json',
        }).toString();
    const r = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (r.ok) {
      const j = await r.json();
      const matches = j?.result?.addressMatches || [];
      if (matches.length > 0) {
        const c = matches[0].coordinates;
        if (c?.x != null && c?.y != null) {
          return { latitude: Number(c.y), longitude: Number(c.x), source: 'census' };
        }
      }
    }
  } catch (e) {
    console.warn('Census geocode failed:', e.message);
  }

  // 2) Nominatim fallback — try full address, then progressively less specific
  const tryNom = async (q) => {
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`,
        { signal: AbortSignal.timeout(8000) }
      );
      if (!r.ok) return null;
      const data = await r.json();
      if (data?.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon),
          source: 'nominatim',
        };
      }
    } catch (e) { /* swallow */ }
    return null;
  };

  if (address) {
    const r = await tryNom(`${address}, ${city || ''}, ${state || ''} ${zip || ''}, USA`);
    if (r) return r;
  }
  if (city && state) {
    const r = await tryNom(`${city}, ${state} ${zip || ''}, USA`);
    if (r) return r;
  }
  if (city && state) {
    const r = await tryNom(`${city}, ${state}, USA`);
    if (r) return r;
  }
  return null;
}
